import { BUSINESS_TYPE } from "@faire/deprecated-retailer-visitor-shared/consts/BUSINESS_TYPE";
import { useRetailerCoreWithSelector } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/RetailerCoreProvider";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

interface RetailerType {
  isNewUser: boolean;
  isGuestRetailer: boolean;
  isOnlineOnlyRetailer: boolean;
  isNonBrickAndMortarRetailer: boolean;
}

const RetailerTypeContext = createContext<StoreApi<RetailerType> | undefined>(
  undefined
);

export const RetailerTypeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);
  const store = useMemo(
    () =>
      createStore<RetailerType>(() => ({
        isNewUser: !(
          retailer?.first_order_at ?? initialRetailer?.first_order_at
        ),
        isGuestRetailer:
          retailer?.is_guest ?? initialRetailer?.is_guest ?? false,
        isOnlineOnlyRetailer:
          !!(retailer?.online_only ?? initialRetailer?.online_only) ||
          (retailer?.business_category ??
            initialRetailer?.business_category) ===
            IRetailer.BusinessCategory.ONLINE_ONLY,
        isNonBrickAndMortarRetailer:
          (retailer?.business_type ?? initialRetailer?.business_type) !==
          BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP,
      })),
    [
      retailer?.first_order_at,
      initialRetailer?.first_order_at,
      retailer?.is_guest,
      initialRetailer?.is_guest,
      retailer?.online_only,
      initialRetailer?.online_only,
      retailer?.business_category,
      initialRetailer?.business_category,
      retailer?.business_type,
      initialRetailer?.business_type,
    ]
  );

  return (
    <RetailerTypeContext.Provider value={store}>
      {children}
    </RetailerTypeContext.Provider>
  );
};

export const useRetailerType = () => {
  const context = useContext(RetailerTypeContext);
  if (!context) {
    throw new Error(
      "RetailerTypeContext not found. Must be used within RetailerTypeProvider"
    );
  }
  return useStore(context);
};
